import { Trans } from '@lingui/macro'
import { Grid, SxProps } from '@mui/material'
import { ContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import { requiredTrans } from 'app/views/forms/formTranslations'
import { Formik, FormikProps } from 'formik'
import { isNil } from 'lodash'
import { FC, useMemo } from 'react'
import * as Yup from 'yup'
import { FormTopLabel } from '../forms/FormTopLabel'
import { TextFieldWithFormik } from '../with-formik/TextFieldWithFormik'

export type ContactFieldsValues = ContactParsed & {
  changeReason?: string
}

interface ContactFieldsProps {
  title?: React.ReactNode
  initialValues?: ContactFieldsValues
  initialTouched?: Partial<Record<keyof ContactFieldsValues, boolean>>
  children?: (formikProps: FormikProps<ContactFieldsValues>) => React.ReactNode
  hiddenFields?: Partial<Record<keyof ContactFieldsValues, boolean>>
  requiredFields?: Partial<Record<keyof ContactFieldsValues, boolean>>
  readonlyFields?: Partial<Record<keyof ContactFieldsValues, boolean>>
  sx?: SxProps
}

export const ContactFields: FC<ContactFieldsProps> = ({
  title,
  initialValues = {},
  initialTouched = {},
  children,
  hiddenFields = { changeReason: true },
  requiredFields = {},
  readonlyFields = {},
  sx = {}
}) => {
  const onSubmit: (values: ContactFieldsValues) => void = () => {}

  const isFieldHidden: (fieldName: keyof ContactFieldsValues) => boolean = (fieldName: keyof ContactFieldsValues) => {
    return hiddenFields[fieldName] === true
  }

  const isFieldRequired: (fieldName: keyof ContactFieldsValues) => boolean = (fieldName: keyof ContactFieldsValues) => {
    return requiredFields[fieldName] === true
  }

  const isFieldReadonly: (fieldName: keyof ContactFieldsValues) => boolean = (fieldName: keyof ContactFieldsValues) => {
    return readonlyFields[fieldName] === true
  }

  const validationSchema = useMemo(() => {
    const schema: Partial<Record<keyof ContactFieldsValues, any>> = {}

    if (isFieldRequired('firstName') && !isFieldHidden('firstName')) {
      schema.firstName = Yup.string().nullable().required(requiredTrans as any)
    }

    if (isFieldRequired('lastName') && !isFieldHidden('lastName')) {
      schema.lastName = Yup.string().nullable().required(requiredTrans as any)
    }

    if (isFieldRequired('email') && !isFieldHidden('email')) {
      schema.email = Yup.string()
        .nullable()
        .required(requiredTrans as any)
        .email(<Trans>This must be a valid email</Trans> as any)
    }

    if (isFieldRequired('phoneNumber') && !isFieldHidden('phoneNumber')) {
      schema.phoneNumber = Yup.string()
        .nullable()
        .required(requiredTrans as any)
        .test(
          'isValidPhone',
          () => <Trans>Phone Number must have at least 10 digits</Trans> as any,
          value => {
            if (!isNil(value)) {
              const filtered = value.replace(/[^0-9]/g, '')
              return filtered.length >= 10
            }
            return true
          }
        )
    }

    if (isFieldRequired('phoneNumberCountryCode') && !isFieldHidden('phoneNumberCountryCode')) {
      schema.phoneNumberCountryCode = Yup.string().nullable().required(requiredTrans as any)
    }

    if (isFieldRequired('providedOrganizationName') && !isFieldHidden('providedOrganizationName')) {
      schema.providedOrganizationName = Yup.string().nullable().required(requiredTrans as any)
    }

    if (isFieldRequired('jobTitle') && !isFieldHidden('jobTitle')) {
      schema.jobTitle = Yup.string().nullable().required(requiredTrans as any)
    }

    if (isFieldRequired('changeReason') && !isFieldHidden('changeReason')) {
      schema.changeReason = Yup.string().nullable().required(requiredTrans as any)
    }

    return Yup.object().shape(schema)
  }, [requiredFields])

  const _sx = {
    border: 'none',
    '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled.Mui-error fieldset': {
      border: 'none !important',
      height: 0
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled.Mui-error input': {
      border: 'none !important',
      height: 0,
      padding: 0
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled input': {
      fontSize: 16,
      fontWeight: 500,
      height: 22,
      padding: '2px 0px',
      '-webkit-text-fill-color': '#000000DE'
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled fieldset': {
      height: 26
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root fieldset': {
      height: 38
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root input': {
      height: 20,
      fontSize: 14,
      padding: '9px 12px'
    },
    '& .MuiFormHelperText-root.Mui-disabled.Mui-error': {
      margin: 0
    },
    '& .MuiFormHelperText-root': {
      margin: 0,
      color: '#000000DE'
    },
    '& .MuiFormHelperText-root .MuiGrid-root.MuiGrid-item .MuiGrid-root.MuiGrid-container': {
      marginTop: '6px'
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      paddingRight: '0px'
    },
    '& .Mui-disabled': {
      backgroundColor: 'transparent'
    },
    '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
      paddingRight: '6px'
    },
    ...sx
  }

  return (
    <Formik<ContactFieldsValues>
      initialValues={{
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        phoneNumberCountryCode: '',
        jobTitle: '',
        providedOrganizationName: '',
        changeReason: '',
        ...initialValues
      }}
      initialTouched={{
        email: false,
        firstName: false,
        lastName: false,
        phoneNumber: false,
        phoneNumberCountryCode: false,
        jobTitle: false,
        providedOrganizationName: false,
        changeReason: false,
        ...initialTouched
      }}
      validateOnMount
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formikProps) => {
        const topLabelSx: (fieldName: keyof ContactFieldsValues) => SxProps = (fieldName) => {
          if (isFieldReadonly(fieldName)) {
            return {
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px'
            }
          }
          return {
            fontSize: 16,
            fontWeight: 700,
            lineHeight: '18px'
          }
        }

        return (
          <>
            {title ?? null}

            {!isFieldHidden('firstName') && !isFieldHidden('lastName') && (
              <Grid
                container
                wrap='nowrap'
                style={{ marginBottom: 32, marginTop: 32 }}
              >
                {!isFieldHidden('firstName') && (
                  <Grid xs item style={{ paddingRight: 5 }}>
                    <TextFieldWithFormik
                      disabled={isFieldReadonly('firstName')}
                      variant='outlined'
                      name='firstName'
                      fullWidth
                      required={isFieldRequired('firstName')}
                      sx={_sx}
                      topLabel={<Trans>First Name</Trans>}
                      topLabelSx={topLabelSx('firstName')}
                    />
                  </Grid>
                )}
                {!isFieldHidden('lastName') && (
                  <Grid xs item style={{ paddingLeft: 5 }}>
                    <TextFieldWithFormik
                      required={isFieldRequired('lastName')}
                      disabled={isFieldReadonly('lastName')}
                      variant='outlined'
                      name='lastName'
                      fullWidth
                      sx={_sx}
                      topLabel={<Trans>Last Name</Trans>}
                      topLabelSx={topLabelSx('lastName')}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {(!isFieldHidden('email') || !isFieldHidden('phoneNumber')) && (
              <Grid
                container
                wrap='nowrap'
                style={{ marginBottom: 32, marginTop: 32 }}
              >
                {!isFieldHidden('email') && (
                  <Grid xs item style={{ paddingRight: 5 }}>
                    <TextFieldWithFormik
                      required={isFieldRequired('email')}
                      disabled={isFieldReadonly('email')}
                      variant='outlined'
                      type='email'
                      name='email'
                      fullWidth
                      sx={_sx}
                      topLabel={<Trans>E-mail</Trans>}
                      topLabelSx={topLabelSx('email')}
                    />
                  </Grid>
                )}
                {!isFieldHidden('phoneNumber') && !isFieldHidden('phoneNumberCountryCode') && (
                  <Grid xs item style={{ paddingLeft: 5 }}>
                    <FormTopLabel
                      required={isFieldRequired('phoneNumber')}
                      small
                      sx={{
                        ...topLabelSx('phoneNumber'),
                        marginBottom: '4px',
                        ...(isFieldReadonly('phoneNumber')
                          ? {
                              fontWeight: 600,
                              fontSize: 14,
                              lineHeight: '20px'
                            }
                          : {})
                      }}
                    >
                      <Trans>Phone number</Trans>
                    </FormTopLabel>
                    <Grid container>
                      {isFieldReadonly('phoneNumberCountryCode') && !isNil(formikProps.errors.phoneNumberCountryCode) && !isNil(formikProps.errors.phoneNumber)
                        ? null
                        : (
                          <Grid item>
                            <TextFieldWithFormik
                              required={isFieldRequired('phoneNumberCountryCode')}
                              disabled={isFieldReadonly('phoneNumberCountryCode')}
                              variant='outlined'
                              name='phoneNumberCountryCode'
                              type='countryCode'
                              sx={{
                                ...(isFieldReadonly('phoneNumberCountryCode') && isNil(formikProps.errors.phoneNumberCountryCode)
                                  ? {
                                      width: `${10 * ((formikProps.values.phoneNumberCountryCode?.length ?? 0) + 1) + 3}px !important`
                                    }
                                  : { paddingRight: '5px' }),
                                ..._sx
                              }}
                            />
                          </Grid>
                          )}
                      <Grid item xs>
                        <TextFieldWithFormik
                          required={isFieldRequired('phoneNumber')}
                          disabled={isFieldReadonly('phoneNumber')}
                          variant='outlined'
                          name='phoneNumber'
                          type='phone'
                          fullWidth
                          sx={_sx}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
            {(!isFieldHidden('providedOrganizationName') || !isFieldHidden('jobTitle')) && (
              <Grid
                container
                wrap='nowrap'
                style={{ marginBottom: 32, marginTop: 32 }}
              >
                {!isFieldHidden('providedOrganizationName') && (
                  <Grid item xs style={{ paddingRight: 5 }}>
                    <TextFieldWithFormik
                      required={isFieldRequired('providedOrganizationName')}
                      disabled={isFieldReadonly('providedOrganizationName')}
                      variant='outlined'
                      name='providedOrganizationName'
                      fullWidth
                      sx={_sx}
                      topLabel={<Trans>Organization</Trans>}
                      topLabelSx={topLabelSx('providedOrganizationName')}
                    />
                  </Grid>
                )}

                {!isFieldHidden('jobTitle') && (
                  <Grid item xs style={{ paddingLeft: 5 }}>
                    <TextFieldWithFormik
                      required={isFieldRequired('jobTitle')}
                      disabled={isFieldReadonly('jobTitle')}
                      variant='outlined'
                      name='jobTitle'
                      limit={80}
                      fullWidth
                      sx={_sx}
                      topLabel={<Trans>Job title</Trans>}
                      topLabelSx={topLabelSx('jobTitle')}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {(!isFieldHidden('changeReason')) && (
              <Grid
                style={{ marginBottom: 32, marginTop: 32 }}
              >
                <TextFieldWithFormik
                  required={isFieldRequired('changeReason')}
                  disabled={isFieldReadonly('changeReason')}
                  variant='outlined'
                  name='changeReason'
                  fullWidth
                  sx={_sx}
                  topLabel={<Trans>CHANGE_REASON_INPUT_LABEL</Trans>}
                  topLabelSx={topLabelSx('changeReason')}
                />
              </Grid>
            )}
            {children?.(formikProps)}
          </>
        )
      }}
    </Formik>
  )
}
